import {
  Button,
  Checkbox,
  IconButton,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseTrigger,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalPositioner,
  ModalTitle,
  Text,
  AlertBanner,
  AlertBannerContent,
  AlertBannerDescription,
  AlertBannerIndicator,
} from "@commonsku/design-system";
import { XIcon } from "@commonsku/styles";
import React, { useState } from "react";
import styled from "styled-components";
import { deleteCustomDomain } from "../../../../actions/customDomain";
import { useDispatch } from "react-redux";

interface CustomDomainDisconnectPopupProps {
  onDisconnected: ({ showFollowUp }: { showFollowUp: boolean }) => void;
  onClose: () => void;
  initialUrl: string;
  customUrl: string | null;
  canDeleteOwnership: boolean;
  shop_id: string;
}

// will be replaced when the design system is updated, there is a conflict between the legacy and new design system css rules, will be removed in the future
const StrongCheckboxLabel = styled(Checkbox.Label)`
  &&& {
    font-weight: bold !important;
  }
`;

const CustomDomainDisconnectPopup = ({
  onDisconnected,
  onClose,
  initialUrl,
  customUrl,
  canDeleteOwnership,
  shop_id,
}: CustomDomainDisconnectPopupProps) => {
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState<boolean>(false);

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      setRequestError(false);
      await dispatch(deleteCustomDomain(shop_id, checked));
      onDisconnected({
        showFollowUp: checked,
      });
    } catch (err) {
      setRequestError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      closeOnEscape
      closeOnInteractOutside
      modal
      motionPreset="scale"
      placement="center"
      role="dialog"
      scrollBehavior="outside"
      size="lg"
      trapFocus
      open
    >
      <ModalBackdrop />
      <ModalPositioner>
        <ModalContent>
          <ModalHeader px="24px" pt="24px" pb="0">
            <ModalTitle fontSize="24px" fontWeight="semibold" mb={1}>
              Disconnect custom domain
            </ModalTitle>
          </ModalHeader>
          <ModalBody pb="4">
            <Text>
              Your shop will go back to its default URL ({initialUrl}) and any
              links pointing to ({customUrl}) will stop working. You can connect
              a new custom domain any time.
            </Text>
            {canDeleteOwnership && (
              <Checkbox.Root
                colorPalette="neutral"
                size="md"
                variant="solid"
                onCheckedChange={(e) => setChecked(!!e.checked)}
                data-testid="customDomain-disconnect-popup.checkbox"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <StrongCheckboxLabel>
                  Also delete the domain record. Reconnecting this domain later
                  will require redoing the DNS setup.
                </StrongCheckboxLabel>
              </Checkbox.Root>
            )}
            {requestError && (
              <AlertBanner status="error" variant="subtle" my="4">
                <AlertBannerIndicator />
                <AlertBannerContent>
                  <AlertBannerDescription>
                    Something went wrong deleting the domain. Please try again.
                  </AlertBannerDescription>
                </AlertBannerContent>
              </AlertBanner>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="customDomain-disconnect-popup-cancel.btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisconnect}
              variant="solid"
              colorPalette="critical"
              disabled={isLoading}
              loading={isLoading}
              data-testid="customDomain-disconnect-popup-disconnect.btn"
            >
              {checked ? "Disconnect and Delete" : "Disconnect"}
            </Button>
          </ModalFooter>
          <ModalCloseTrigger asChild>
            <IconButton
              onClick={onClose}
              size="xl"
              label="Close"
              variant="ghost"
            >
              <XIcon />
            </IconButton>
          </ModalCloseTrigger>
        </ModalContent>
      </ModalPositioner>
    </Modal>
  );
};

export default CustomDomainDisconnectPopup;
